import React, { useEffect, useRef, useState } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import { axiosInstance } from '../Config'
import Pagination, { PerPageSelect } from '../../components/Pagination'
import categoryImg from "../../Assets/Images/categories.png"
import { usePermissionData } from '../../AppContext'

const AGE_MIN = 4
const AGE_MAX = 16

const labelStyle = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }

/* ── Dual-thumb range slider ── */
const AgeRangeSlider = ({ ageFrom, ageTo, onChange }) => {
    const pctFrom = ((ageFrom - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100
    const pctTo   = ((ageTo   - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100

    return (
        <div>
            <style>{`
                .age-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    background: transparent;
                    pointer-events: none;
                    position: absolute;
                    width: 100%;
                    top: 50%;
                    transform: translateY(-50%);
                    margin: 0;
                    height: 0;
                    outline: none;
                    border: none;
                }
                .age-thumb::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #A44CD7, #0CACC2);
                    cursor: pointer;
                    pointer-events: all;
                    border: 3px solid #fff;
                    box-shadow: 0 2px 8px rgba(164,76,215,0.40);
                }
                .age-thumb::-moz-range-thumb {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #A44CD7, #0CACC2);
                    cursor: pointer;
                    pointer-events: all;
                    border: 3px solid #fff;
                    box-shadow: 0 2px 8px rgba(164,76,215,0.40);
                }
            `}</style>

            {/* Value display */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-center">
                    <p className="mb-0" style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>From Age</p>
                    <span className="fw-bold" style={{ fontSize: 22, color: '#A44CD7' }}>{ageFrom}</span>
                </div>
                <div className="text-center flex-grow-1">
                    <span className="badge rounded-pill px-3 py-1"
                        style={{ background: 'linear-gradient(135deg, rgba(164,76,215,0.12), rgba(12,172,194,0.12))', color: '#A44CD7', fontSize: 13, fontWeight: 600 }}>
                        Age Group {ageFrom} – {ageTo}
                    </span>
                </div>
                <div className="text-center">
                    <p className="mb-0" style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>To Age</p>
                    <span className="fw-bold" style={{ fontSize: 22, color: '#0CACC2' }}>{ageTo}</span>
                </div>
            </div>

            {/* Slider track */}
            <div style={{ position: 'relative', height: 40 }}>
                {/* Gray background track */}
                <div style={{
                    position: 'absolute', top: '50%', left: 0, right: 0,
                    height: 6, background: '#e5e7eb', borderRadius: 3,
                    transform: 'translateY(-50%)',
                }} />
                {/* Colored fill between thumbs */}
                <div style={{
                    position: 'absolute', top: '50%',
                    left: `${pctFrom}%`, width: `${pctTo - pctFrom}%`,
                    height: 6, background: 'linear-gradient(90deg, #A44CD7, #0CACC2)',
                    borderRadius: 3, transform: 'translateY(-50%)',
                }} />

                {/* From thumb — higher z-index when at max so it stays grabbable */}
                <input
                    type="range"
                    className="age-thumb"
                    min={AGE_MIN} max={AGE_MAX}
                    value={ageFrom}
                    style={{ zIndex: ageFrom >= ageTo ? 5 : 3 }}
                    onChange={e => {
                        const v = Number(e.target.value)
                        onChange(Math.min(v, ageTo), ageTo)
                    }}
                />
                {/* To thumb */}
                <input
                    type="range"
                    className="age-thumb"
                    min={AGE_MIN} max={AGE_MAX}
                    value={ageTo}
                    style={{ zIndex: 4 }}
                    onChange={e => {
                        const v = Number(e.target.value)
                        onChange(ageFrom, Math.max(v, ageFrom))
                    }}
                />
            </div>

            {/* Min / Max labels */}
            <div className="d-flex justify-content-between mt-1">
                <span style={{ fontSize: 11, color: '#bbb' }}>{AGE_MIN}</span>
                <span style={{ fontSize: 11, color: '#bbb' }}>{AGE_MAX}</span>
            </div>
        </div>
    )
}

const Category = () => {
    const { checkAccess } = usePermissionData()
    const [categories, setCategories]     = useState([])
    const [filtered, setFiltered]         = useState([])
    const [loading, setLoading]           = useState(false)
    const [searchQuery, setSearchQuery]   = useState('')

    const [page, setPage]                 = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)

    const [showModal, setShowModal]       = useState(false)
    const [modalMode, setModalMode]       = useState('add')
    const [editingItem, setEditingItem]   = useState(null)
    const [inputName, setInputName]       = useState('')
    const [ageFrom, setAgeFrom]           = useState(AGE_MIN)
    const [ageTo, setAgeTo]               = useState(AGE_MAX)
    const [nameError, setNameError]       = useState('')
    const [submitting, setSubmitting]     = useState(false)
    const searchTimerRef                  = useRef(null)

    useEffect(() => {
        document.title = 'Category | Mindora'
        window.scrollTo(0, 0)
        fetchCategories('')
        return () => clearTimeout(searchTimerRef.current)
    }, [])

    const fetchCategories = (search = '') => {
        setLoading(true)
        axiosInstance.get('categories', { params: search ? { search } : {} })
            .then(res => {
                const list = res.data?.data ?? res.data ?? []
                setCategories(list.reverse())
                setFiltered(list)
            })
            .catch(err => swal('', err?.response?.data?.message || 'Failed to load categories.', 'error'))
            .finally(() => setLoading(false))
    }

    const onSearch = (e) => {
        const q = e.target.value
        setSearchQuery(q)
        setPage(1)
        clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => fetchCategories(q), 500)
    }

    const openAdd = () => {
        setModalMode('add')
        setEditingItem(null)
        setInputName('')
        setAgeFrom(AGE_MIN)
        setAgeTo(AGE_MAX)
        setNameError('')
        setShowModal(true)
    }

    const openEdit = (item) => {
        setModalMode('edit')
        setEditingItem(item)
        setInputName(item.categoryName)
        setAgeFrom(item.ageFrom ?? AGE_MIN)
        setAgeTo(item.ageTo ?? AGE_MAX)
        setNameError('')
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setEditingItem(null)
        setInputName('')
        setAgeFrom(AGE_MIN)
        setAgeTo(AGE_MAX)
        setNameError('')
    }

    const handleSubmit = () => {
        const trimmed = inputName.trim()
        if (!trimmed) { setNameError('Please enter a category name.'); return }

        setSubmitting(true)
        const body = { categoryName: trimmed, ageFrom, ageTo }

        const request = modalMode === 'edit'
            ? axiosInstance.put(`categories/${editingItem.id}`, body)
            : axiosInstance.post('categories', body)

        request
            .then(res => {
                swal('', res.data?.message || (modalMode === 'edit' ? 'Category updated successfully!' : 'Category added successfully!'), 'success')
                closeModal()
                fetchCategories(searchQuery)
            })
            .catch(err => setNameError(err?.response?.data?.message || 'Something went wrong.'))
            .finally(() => setSubmitting(false))
    }

    const handleDelete = (item) => {
        swal({
            title: `Delete "${item.categoryName}"?`,
            text: 'This category will be permanently removed.',
            icon: 'warning',
            buttons: true,
            dangerMode: true,
        }).then(will => {
            if (will) {
                setLoading(true)
                axiosInstance.delete(`categories/${item.id}`)
                    .then(res => {
                        swal('', res.data?.message || 'Deleted successfully!', 'success')
                        fetchCategories(searchQuery)
                    })
                    .catch(err => swal('', err?.response?.data?.message || 'Failed to delete.', 'error'))
                    .finally(() => setLoading(false))
            }
        })
    }

    const totalPages = Math.ceil(filtered.length / itemsPerPage)
    const paged      = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage)

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title="Category" description="Manage video categories for the app">
                <section className="ps-dashboards p-4">

                    {/* Banner */}
                    <div className="rounded-4 p-4 mb-4 position-relative overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, #A44CD7 0%, #0CACC2 100%)' }}>
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                        <div style={{ position: 'absolute', bottom: -40, right: 160, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <div className="position-relative d-flex align-items-center gap-3">
                            <span style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }}><img height={40} src={categoryImg} /></span>
                            <div className="flex-grow-1">
                                <h5 className="fw-bold mb-1" style={{ color: '#fff' }}>Categories</h5>
                                <p className="mb-0" style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14 }}>
                                    Categories are used to organise videos in the app for kids by age group.
                                </p>
                            </div>
                            {checkAccess(14, 2) && (
                                <button type="button" className="button btn-green" style={{ flexShrink: 0 }} onClick={openAdd}>
                                    <i className="icon-plus me-2"></i>Add Category
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Stats + Search */}
                    <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-wrap">
                        <div className="rounded-4 p-3 d-flex align-items-center gap-3"
                            style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 200 }}>
                            <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 48, height: 48, background: 'rgba(164,76,215,0.10)' }}>
                                <i className="bi bi-grid" style={{ fontSize: 22, color: '#A44CD7' }}></i>
                            </div>
                            <div>
                                <p className="mb-0" style={labelStyle}>Total Categories</p>
                                <h4 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{categories.length}</h4>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <div className="ps-form--search-simple">
                                <input
                                    className="form-control bg-white"
                                    type="text"
                                    value={searchQuery}
                                    onChange={onSearch}
                                    placeholder="Search categories..."
                                />
                                <button type="button">
                                    <i className="icon icon-magnifier"></i>
                                </button>
                            </div>
                            <PerPageSelect itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} setPage={setPage} />
                        </div>
                    </div>

                    {/* Section heading */}
                    <h6 className="fw-bold mb-3" style={{ color: '#1a1a2e', fontSize: 16 }}>
                        Category List
                        <span className="ms-2 badge rounded-pill"
                            style={{ background: 'rgba(164,76,215,0.12)', color: '#A44CD7', fontSize: 12 }}>
                            {filtered.length}
                        </span>
                        {searchQuery && (
                            <span className="ms-2 text-muted fw-normal" style={{ fontSize: 13 }}>
                                — results for "{searchQuery}"
                            </span>
                        )}
                    </h6>

                    {/* Table */}
                    {filtered.length === 0 ? (
                        <div className="text-center py-5" style={{ color: '#aaa' }}>
                            <i className="bi bi-grid" style={{ fontSize: 52, opacity: 0.3 }}></i>
                            <p className="mt-3 mb-0">
                                {searchQuery ? `No categories match "${searchQuery}".` : 'No categories added yet.'}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12 }}>
                        <table className="jean-table table" style={{ minWidth: 700 }}>
                            <tbody>
                                {paged.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td style={{ width: 48 }}>
                                            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                                style={{ width: 38, height: 38, background: 'rgba(164,76,215,0.12)', color: '#A44CD7', fontSize: 13 }}>
                                                {(page - 1) * itemsPerPage + idx + 1}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{ width: 40, height: 40, background: 'linear-gradient(135deg, rgba(164,76,215,0.15), rgba(12,172,194,0.15))' }}>
                                                    <i className="bi bi-tag" style={{ fontSize: 16, color: '#A44CD7' }}></i>
                                                </div>
                                                <div>
                                                    <p style={labelStyle} className="mb-0">Category Name</p>
                                                    <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e', fontSize: 15 }}>{item.categoryName}</p>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <p style={labelStyle} className="mb-1">Age Group</p>
                                            {item.ageFrom != null && item.ageTo != null ? (
                                                <span className="badge rounded-pill px-3 py-1"
                                                    style={{ background: 'linear-gradient(135deg, rgba(164,76,215,0.12), rgba(12,172,194,0.12))', color: '#A44CD7', fontWeight: 600, fontSize: 12 }}>
                                                    <i className="bi bi-person-heart me-1"></i>
                                                    {item.ageFrom} – {item.ageTo}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: 13, color: '#bbb' }}>—</span>
                                            )}
                                        </td>

                                        <td>
                                            <p style={labelStyle} className="mb-0">Created</p>
                                            <p className="mb-0" style={{ fontSize: 13, color: '#555' }}>
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </p>
                                        </td>

                                        <td className="pe-3">
                                            <div className="d-flex justify-content-end gap-2">
                                                {checkAccess(14, 3) && (
                                                    <button type="button" className="btn fw-semibold"
                                                        style={{ background: '#f5eeff', color: '#A44CD7', borderRadius: 8, padding: '5px 14px', fontSize: 12, border: '1px solid #e0c8ff' }}
                                                        onClick={() => openEdit(item)}>
                                                        <i className="bi bi-pencil me-1"></i>Edit
                                                    </button>
                                                )}
                                                {checkAccess(14, 4) && (
                                                    <button type="button" className="btn fw-semibold"
                                                        style={{ background: '#fff0f0', color: '#e03131', borderRadius: 8, padding: '5px 14px', fontSize: 12, border: '1px solid #ffd0d0' }}
                                                        onClick={() => handleDelete(item)}>
                                                        <i className="icon-trash me-1"></i>Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                        </table>
                        </div>
                    )}

                    <Pagination
                        page={page} totalPages={totalPages}
                        setPage={setPage} totalItems={filtered.length}
                        showingCount={paged.length}
                    />

                </section>
            </ContainerDashboard>

            {/* Add / Edit Modal */}
            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.48)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={e => { if (e.target === e.currentTarget) closeModal() }}>
                    <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.20)' }}>

                        <div className="d-flex align-items-center gap-3 px-4 py-3"
                            style={{ background: 'linear-gradient(135deg, #A44CD7, #0CACC2)' }}>
                            <span style={{ fontSize: 26 }}>{modalMode === 'edit' ? '✏️' : '🗂️'}</span>
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold text-white">
                                    {modalMode === 'edit' ? 'Edit Category' : 'Add Category'}
                                </h6>
                                <p className="mb-0" style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                                    {modalMode === 'edit' ? 'Update the category details.' : 'Add a new category for videos.'}
                                </p>
                            </div>
                            <button onClick={closeModal}
                                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                ✕
                            </button>
                        </div>

                        <div className="p-4">
                            {/* Category Name */}
                            <div className="mb-4">
                                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                                    Category Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${nameError ? 'is-invalid' : ''}`}
                                    style={{ borderRadius: 10, fontSize: 14 }}
                                    placeholder="e.g. Learning, Math, Nature..."
                                    value={inputName}
                                    autoFocus
                                    onChange={e => { setInputName(e.target.value); setNameError('') }}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
                                />
                                {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
                            </div>

                            {/* Age Group Slider */}
                            <div className="mb-4">
                                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                                    <i className="bi bi-person-heart me-1" style={{ color: '#A44CD7' }}></i>
                                    Age Group
                                </label>
                                <div className="rounded-3 p-3"
                                    style={{ background: '#fdf5ff', border: '1px solid rgba(164,76,215,0.15)' }}>
                                    <AgeRangeSlider
                                        ageFrom={ageFrom}
                                        ageTo={ageTo}
                                        onChange={(from, to) => { setAgeFrom(from); setAgeTo(to) }}
                                    />
                                </div>
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn px-4"
                                    style={{ borderRadius: 10, background: '#f5f5f5', color: '#555', fontSize: 13 }}
                                    onClick={closeModal}>
                                    Cancel
                                </button>
                                <button className="btn px-4 fw-semibold text-white" disabled={submitting}
                                    style={{ borderRadius: 10, background: 'linear-gradient(135deg, #A44CD7, #0CACC2)', fontSize: 13, border: 'none' }}
                                    onClick={handleSubmit}>
                                    <i className="bi bi-check2 me-1"></i>
                                    {submitting ? 'Saving…' : modalMode === 'edit' ? 'Save Changes' : 'Add Category'}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </>
    )
}

export default Category
